const { vestResolver } = require('@hookform/resolvers/vest')
import { csx, Flex, Text } from '@vtex/admin-ui'
import { Select, useFormState } from '@vtex/admin-ui-form'
import { useCallback, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DiscountType, ItemsToConsider, OrderValue } from 'typings'
import {
  CancelButton,
  CurrencyInput,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  OrderValueConfig,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../..'
import { ItemsReferencePriceAlert } from '../shared/ItemsReferencePriceAlert'
import { messages } from './messages'
import { validationSuite } from './validation'

const EMPTY_CONFIG = Object.freeze({
  min: undefined,
  max: undefined,
  itemsToConsider: undefined,
})

interface Props {
  state: UseDrawerStateReturn
  initialConfig: OrderValueConfig
  discountType: DiscountType | undefined
  onSave: (config: OrderValueConfig) => void
}

export function Editor(props: Props) {
  const { state, onSave, initialConfig, discountType } = props
  const { hide } = state

  const isCreating = initialConfig === undefined
  const intl = useIntl()
  const { $t } = intl

  const formValidationSuite = useMemo(() => validationSuite(intl), [intl])

  const drawerForm = useFormState<OrderValue>({
    defaultValues: {
      ...EMPTY_CONFIG,
    },
    resolver: vestResolver(formValidationSuite),
  })

  const setInitialConfig = useCallback(() => {
    const values = {
      ...EMPTY_CONFIG,
      ...initialConfig,
    }
    drawerForm.reset(values)
  }, [drawerForm, initialConfig])

  useEffect(() => {
    state.open && setInitialConfig()
  }, [setInitialConfig, state.open])

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()

    if (isValid) {
      const values = drawerForm.getValues()
      const hasOrderValue = values.min || values.max
      onSave(hasOrderValue ? values : undefined)
      hide()
    }
  }

  const itemsToConsiderOptions: Array<{
    value: ItemsToConsider
    label: string
  }> = [
    { value: 'IncludeMatchedItems', label: $t(messages.includeMatchedItems) },
    {
      value: 'ExcludeMatchedItems',
      label: $t(messages.excludeMatchedItems),
    },
    { value: 'AllItems', label: $t(messages.allItems) },
  ]

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        {(!discountType || discountType === DiscountType.Price) && (
          <DrawerSubtitle>
            <FormattedMessage
              {...messages.subtitle}
              values={{
                b: (content) => <Text variant="action1">{content}</Text>,
              }}
            />
          </DrawerSubtitle>
        )}
        <ItemsReferencePriceAlert discountType={discountType} mode="order" />
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Flex className={csx({ columnGap: '$space-5', '>div': { flex: 1 } })}>
          <CurrencyInput
            name="min"
            label={$t(messages.min)}
            state={drawerForm}
          />
          <CurrencyInput
            name="max"
            label={$t(messages.max)}
            state={drawerForm}
          />
        </Flex>
        <Select
          name="itemsToConsider"
          defaultValue={itemsToConsiderOptions[0].value}
          label={$t(messages.select)}
          className={csx({ marginTop: '$space-5' })}
          state={drawerForm}
        >
          {itemsToConsiderOptions.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
